import React, { useState, useRef, useEffect, MouseEvent } from "react";
import { Search, Code, Palette, Share2, Target, ChartBar, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  id: "wordpress",
  title: "WordPress Design & Hosting",
  description: "Custom WordPress solutions designed for speed, security, and seamless user experiences.",
  icon: Code,
  linkText: "EXPLORE_WEB",
  href: "/ServicesPage"
}, {
  id: "seo",
  title: "SEO Optimization",
  description: "Data-driven search strategies to boost your online visibility and dominate your market.",
  icon: Search,
  linkText: "EXPLORE_SEO",
  href: "/ServicesPage"
}, {
  id: "google-business",
  title: "Google Business Profile",
  description: "Comprehensive local SEO management to ensure your business stands out on Google Maps.",
  icon: Target,
  linkText: "EXPLORE_LOCAL",
  href: "/ServicesPage"
}, {
  id: "social-media",
  title: "Social Media Growth",
  description: "Targeted social campaigns that build community and amplify your unique brand voice.",
  icon: Share2,
  linkText: "EXPLORE_SOCIAL",
  href: "/ServicesPage"
}, {
  id: "content-creation",
  title: "Content Creation",
  description: "Engaging visual and written storytelling that captivates your audience and drives conversion.",
  icon: Palette,
  linkText: "EXPLORE_CONTENT",
  href: "/ServicesPage"
}, {
  id: "training",
  title: "Training & Support",
  description: "Personalized coaching to help you master your digital tools and grow with confidence.",
  icon: ChartBar,
  linkText: "EXPLORE_TRAINING",
  href: "/ServicesPage"
}];
interface SpotlightCardProps {
  service: typeof services[0];
  index: number;
}
const SpotlightCard = ({
  service,
  index
}: SpotlightCardProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [opacity, setOpacity] = useState(0);
  const {
    ref: scrollRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  const handleMouseEnter = () => {
    setOpacity(1);
  };
  const handleMouseLeave = () => {
    setOpacity(0);
  };
  const setRefs = (node: HTMLDivElement) => {
    divRef.current = node;
    scrollRef.current = node;
  };
  const Icon = service.icon;
  return <div data-section-id="16411" ref={setRefs} onMouseMove={handleMouseMove} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5 backdrop-blur-md transition-all duration-500 hover:border-primary/50 hover:-translate-y-[10px] hover:shadow-[0_20px_50px_-12px_rgba(20,255,255,0.15)]" data-index={index}>
      {}
      <div className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
      background: `radial-gradient(600px circle at ${position.x}px ${position.y}px, hsl(var(--primary) / 0.15), transparent 40%)`
    }} />

      <Card className="relative z-10 h-full border-none bg-transparent shadow-none">
        <CardHeader className="pb-4">
          <div className="mb-4 inline-flex size-14 items-center justify-center bg-primary/10 text-primary" style={{
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
        }}>
            <Icon className={`size-6 transition-all duration-1000 ${isVisible ? "animate-draw" : "opacity-0"}`} style={{
            strokeDasharray: 100,
            strokeDashoffset: isVisible ? 0 : 100
          }} />
          </div>
          <CardTitle className="font-serif text-xl font-bold tracking-tight text-foreground">
            {service.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="grow">
          <p className="text-muted-foreground font-sans text-sm leading-relaxed">
            {service.description}
          </p>
        </CardContent>
        <CardFooter className="pt-4">
          <Link to={service.href} className="group/link flex items-center gap-2 font-mono text-[10px] font-bold tracking-widest text-primary transition-colors hover:text-primary/80">
            <span className="opacity-50">&gt;</span> {service.linkText}
            <ArrowRight className="size-3 -translate-x-1 opacity-0 transition-all duration-300 group-hover/link:translate-x-0 group-hover/link:opacity-100" />
          </Link>
        </CardFooter>
      </Card>
    </div>;
};
export default function TheGrowthEcosystem() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="growth-driven-solutions" className="dark bg-[#0a0814] text-foreground relative overflow-hidden py-24 sm:py-32 grid-pattern">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-20 left-20 size-96 rounded-full bg-primary/5 blur-3xl opacity-30" />
        <div className="absolute bottom-20 right-20 size-96 rounded-full bg-primary/5 blur-3xl opacity-30" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-2xl text-center mb-16 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-serif text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl mb-4">
            Growth-Driven <span className="text-primary">Solutions</span>
          </h2>
          <p className="text-muted-foreground font-sans text-lg">
            Building relationships, not just businesses. We provide premium digital ecosystems engineered to scale your online presence and confidence.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {services.map((service, i) => <SpotlightCard key={service.id} service={service} index={i} />)}
        </div>
      </div>

      <style jsx global>{`
        @keyframes draw {
          from {
            stroke-dashoffset: 100;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
        .animate-draw {
          animation: draw 1.5s ease-out forwards;
        }
        .grid-pattern {
          background-image: linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px),
                            linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>
    </section>;
}